<template>
  <div class="lead_resume">
    <v-card outlined>
      <v-card-title>
        {{ $capitalize($tc("model.contact_attempt_insured_resume_component_title")) }}
        <v-spacer />
        <v-menu offset-x v-model="resent_menu" :close-on-content-click="false">
          <template v-slot:activator="{on, attrs}">
            <v-btn
              v-bind="attrs" 
              v-on="on"
              icon
              :disabled="!has_three_failed_attempts"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              {{ $tc('model.contact_attempt_resend_sms_letter_menu_title' )}}
            </v-card-title>
            <v-card-text>
              <ValidationObserver ref="resend_form">
                <ValidationProvider name="contact_attempt.participant_number" rules="required" v-slot="{ errors }">
                  <v-combobox
                    :label="
                      $capitalize($tc('model.contact_attempt_participant_number_label'))
                    "
                    v-model="resend_message_number"
                    :items="contactNumbers"
                    item-value="number"
                    item-text="number"
                    :return-object="false"
                    :error-messages="errors"
                    clearable
                  />
                </ValidationProvider>
              </ValidationObserver>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="resendMessages" :loading="resending">
                {{ $tc('model.contact_attempt_resend_button') }}
              </v-btn>
            </v-card-actions>

          </v-card>
        </v-menu>
        <v-btn
          color="primary"
          icon
          @click="openDialog"
          :disabled="has_three_failed_attempts"
        >
          <v-icon>mdi-phone-outline</v-icon>
        </v-btn>
        <v-btn
          icon
          @click="fetchContactAttempts"
          :loading="fetching"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <empty-state 
        :when="contact_attempts.length == 0"
        :message="$tc('model.contact_attempt_no_contact_attempt_for_insured_found_message')"
      >
        <v-list>
          <template v-for="(attempt, index) in contact_attempts">
            <v-list-item :key="'item-' + attempt.id">
              <v-list-item-icon>
                <v-icon v-if="attempt.success" color="green lighten-2" class="pt-3">
                  mdi-phone-check
                </v-icon>
                <v-icon v-else color="red lighten-2" class="pt-3">
                  mdi-phone-off
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                {{
                  attempt.justification ||
                    $capitalize($tc("model.contact_attempt_no_justification_text"))
                }}</v-list-item-title>
                <v-list-item-subtitle>{{
                  getContactAttemptDuration(attempt)
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mx-4" v-if="index < contact_attempts.length-1" :key="'divider-' + attempt.id" />
          </template>
        </v-list>
      </empty-state>
    </v-card>
    <v-dialog v-model="attempt_dialog" max-width="600px" persistent>
      <v-card>
        <v-app-bar flat>
          <v-toolbar-title class="font-weight-medium">
            {{ $tc("model.contact_attempt_insured_resume_dialog_title")}}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="handleCloseDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-app-bar>
        <ValidationObserver ref="form">
          <v-form @submit.prevent="handleSubmit">
            <v-card-text>
              <v-row class="my-4" dense>
                <v-col cols="6">
                  <h3>{{ $capitalize($tc("model.contact_attempt_when_the_contact_attempt_happened_subtitle")) }}</h3>
                </v-col>
                <v-col  cols="6" class="text-right d-flex">
                  <v-btn small @click="setNow()" text class="ml-auto">
                    <v-icon size="15">mdi-update</v-icon>{{ $capitalize($tc("model.contact_attempt_set_now_button_label")) }}
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6">
                  <ValidationProvider
                    name="contact_attempt.date"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <date-picker
                      :label="$capitalize($tc('model.contact_attempt_date_label'))"
                      name="contact_attempt.date"
                      v-model="form.date"
                      :error_messages="errors"
                      clearable
                    ></date-picker>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" sm="6">
                  <ValidationProvider
                    name="contact_attempt.time"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <time-picker
                      :label="$capitalize($tc('model.contact_attempt_time_label'))"
                      name="contact_attempt.time"
                      v-model="form.time"
                      :error_messages="errors"
                      format="24hr"
                    ></time-picker>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <h3>{{ $capitalize($tc("model.contact_attempt_could_insured_be_contacted_subtitle")) }}</h3>
                </v-col>
                <v-col cols="12">
                  <v-btn-toggle v-model="form.success" dense mandatory>
                    <v-btn
                      color="red--text"
                      :value="false"
                    >
                      <v-icon color="red">mdi-close</v-icon>
                      <span v-if="breakpoint !== 'xs' && breakpoint !== 'sm'">
                        {{ $capitalize($tc("model.contact_attempt_failure_button")) }}
                      </span>
                    </v-btn>

                    <v-btn
                      color="green--text"
                      :value="true"
                    >
                      <v-icon color="green">mdi-check</v-icon>
                      <span v-if="breakpoint !== 'xs' && breakpoint !== 'sm'"
                        >{{ $capitalize($tc("model.contact_attempt_success_button")) }}</span
                      >
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
              <v-row dense v-if="!form.success">
                <v-col>
                  <ValidationProvider name="contact_attempt.justification" :rules="!form.success ? 'required' : ''" v-slot="{ errors }">
                    <v-textarea
                      v-model="form.justification"
                      :error-messages="errors"
                      name="contact_attempt.justification"
                      :label="
                        $capitalize($tc('model.contact_attempt_justification_label'))
                      "
                      filled
                      class="mt-3"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row dense v-if="needs_letter && !form.success">
                <v-col cols="12" class="my-0">
                  <h3>{{ $capitalize($tc("model.contact_attempt_participant_number_send_message_subtitle")) }}</h3>
                  <h5 class="red--text">{{ $capitalize($tc("model.contact_attempt_reason_for_participant_number_subtitle")) }}</h5>
                </v-col>
                <v-col>
                  <ValidationProvider name="contact_attempt.participant_number" :rules="needs_letter && !form.success ? 'required' : ''" v-slot="{ errors }">
                    <v-combobox
                      :label="
                        $capitalize($tc('model.contact_attempt_participant_number_label'))
                      "
                      v-model="form.participant_number"
                      :items="contactNumbers"
                      item-value="number"
                      item-text="number"
                      :return-object="false"
                      :error-messages="errors"
                      clearable
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn text @click="handleCloseDialog">{{ $capitalize($tc("model.interface_cancel")) }}</v-btn>
              <v-btn
                type="submit"
                color="primary"
                dark
                :loading="saving"
              >{{ $capitalize($tc("model.interface_save_button")) }}</v-btn>
            </v-card-actions>
          </v-form>
        </ValidationObserver>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import DatePicker from "@/components/DatePicker";
import TimePicker from "@/components/TimePicker";
import EmptyState from "@/components/EmptyState";
import { listTimezones } from '@/tools/date';
import { indexContactAttempt, storeContactAttempt, resendMessages } from '@/services/contact_attempts';
export default {
  name: "contact-attempt",
  components: { DatePicker, TimePicker, EmptyState },
  data: () => ({
    resent_menu: false,
    fetching: false,
    saving: false,
    resending: false,
    contact_attempts: [],
    attempt_dialog: false,
    form: {
      date: null,
      time: null,
      success: true,
      justification: null
    },
    resend_message_number: null
  }),
  methods: {
    async fetchContactAttempts() {
      this.fetching = true;
      try {
        const response = await indexContactAttempt({
          lead_identifier_code: this.$route.params["lead_number"],
          is_insured: true
        });
        this.contact_attempts = response;
      } catch (e) {
        this.$store.commit('sendMessage', { 
          text: this.$tc('model.contact_attempt_fetch_error_message'),
          color: 'red'
        });
      } finally {
        this.fetching = false;
      }
    },
    getContactAttemptDuration(attempt) {
      const attempt_moment = this.$moment(`${attempt.date} ${attempt.time}`);
      const now = this.$moment();

      return this.$moment.duration(attempt_moment.diff(now)).humanize(true);
    },
    openDialog() {
      this.attempt_dialog = true;
    },
    handleCloseDialog() {
      this.attempt_dialog = false;
      this.form = {
        date: null,
        time: null,
        success: true,
        justification: null
      };
      this.$refs.form.reset();
    },
    async handleSubmit() {
      const validation = await this.$refs.form.validate();
      if (!validation) return;

      this.saving = true;
      try {
        await storeContactAttempt({
          contact_attempt: {
            participant_id: this.lead.participant_insured?.id,
            ...this.form
          }
        });
        this.handleCloseDialog();
        this.fetchContactAttempts();
      } catch (e) {
        if (e.response.data.errors) {
          this.$refs.form.setErrors(e.response.data.errors);
        }

        this.$store.commit('sendMessage', {
          text: this.$tc('model.contact_attempt_save_attempt_error_message'),
          color: 'red'
        });
      } finally {
        this.saving = false;
      }
    },
    async resendMessages() {
      const validation = await this.$refs.resend_form.validate();
      if (!validation) return;

      this.resending = true;
      try {
        await resendMessages(this.lead.participant_insured?.id, {
          contact_attempt: {
            participant_number: this.resend_message_number
          }
        });
        this.$store.commit('sendMessage', {
          text: this.$tc('model.contact_attempt_resend_messages_success_message'),
          color: 'green'
        });
      } catch (e) {
        if (e.response.data.errors) {
          this.$refs.resend_form.setErrors(e.response.data.errors);
        }

        this.$store.commit('sendMessage', {
          text: this.$tc('model.contact_attempt_resend_messages_error_message'),
          color: 'red'
        });
      } finally {
        this.resending = false;
      }      
    },
    getMinAllowedTime() {
      return "00:00";
    },
    getMaxAllowedTime() {
      if (
        this.$moment(this.form.date).isSame(this.$moment(), "day")
      ) {
        return this.$moment().format("HH:mm");
      } else {
        return "23:59";
      }
    },
    setNow() {
      const now = this.$moment();

      this.form.time =now.format("HH:mm");
      this.form.date = now.format("YYYY-MM-DD");
    }
  },
  computed: {
    has_three_failed_attempts() {
      const three_attempts = this.contact_attempts.slice(0, 3);
      
      return this.contact_attempts.length >= 3 && three_attempts.every(attempt => !attempt.success);
    },
    needs_letter() {
      const two_attempts = this.contact_attempts.slice(0, 2);
      
      return this.contact_attempts.length == 2 && two_attempts.every(attempt => !attempt.success);
    },
    breakpoint: function() {
      return this.$vuetify.breakpoint.name;
    },
    lead: function () {
      return this.$store.getters.getLead;
    },
    contactNumbers: function () {
      const contact1 = { type: 'contact1', number: this.lead.participant_insured?.contact?.contact1 };
      const contact2 = { type: 'contact2', number: this.lead.participant_insured?.contact?.contact2 };

      return contact2.number ? [contact1, contact2] : [contact1];      
    },
  },
  created() {
    this.fetchContactAttempts();
  }
};
</script>